'use strict'

import State from 'ampersand-state'
import Collection from 'ampersand-rest-collection'
import Observable from './_observable'
import {result, each, isString} from 'lodash/fp'
import flow from 'lodash/flow'
import {create as createStream} from '@most/create'
import {
  combineArray as combineStreams
} from 'most'

const PeerModel = State.extend(Observable).extend({
  props: {
    id: 'any',
    displayName: 'string',
    isLocal: ['boolean', true, false],
    isHost: ['boolean', true, false],
    isWebcamLive: ['boolean', true, false],
    isScreenLive: ['boolean', true, false],
    isUser: ['boolean', true, false], // Is this a human peer or an external service feed?
    isVisible: ['boolean', true, false],
    isMuted: ['boolean', true, false],
    webcamStream: 'object',
    screenStream: 'object',
    feedStream: 'object',
    device: 'object', // Browser/device info (not device model)
    speakerDeviceId: 'string', // Refers to user media 'audiooutput'
    microphoneDeviceId: 'string',
    webcamDeviceId: 'string',
    screenDeviceId: 'string',
    audioDeviceId: 'string',
    videoFeed: 'state', // Non-users don't have media based on device, they just have a generic "feed"
    audioFeed: 'state', // Non-users don't have media based on device, they just have a generic "feed"
    microphone: 'state',
    webcam: 'state',
    screen: 'state',
    audio: 'state',
  },
  collections: {

  },
  initialize (options = {}) {
    this.addMedia = options.addMedia

    if (this.isLocal) {
      // Media props are set directly on local peer, and their IDs sent to other peers
      this.listenToAndRun(this, 'change:microphone', () => this.microphoneDeviceId = result('microphone.deviceId')(this))
      this.listenToAndRun(this, 'change:webcam', () => this.webcamDeviceId = result('webcam.deviceId')(this))
      this.listenToAndRun(this, 'change:screen', () => this.screenDeviceId = result('screen.deviceId')(this))
      this.listenToAndRun(this, 'change:audio', () => this.audioDeviceId = result('audio.deviceId')(this))
    } else {
      // Media IDs are received for remote peers, and their media is derived from it
      if (this.isUser) {
        // Users have slots for device types
        this.listenToAndRun(this, 'change:microphoneDeviceId change:webcamDeviceId change:screenDeviceId change:audioDeviceId', () => {
          this.updateMediaByOwnDeviceIds()
        })
      }
    }

    const combineTracks = (videoTrack, audioTrack) => {
      const stream = new MediaStream()
      if (!(videoTrack || audioTrack)) return null
      videoTrack && stream.addTrack(videoTrack)
      audioTrack && stream.addTrack(audioTrack)
      return stream
    }

    this.webcam$ = combineStreams(combineTracks, [
      this.streamOfChildProperty('webcam', 'track'),
      this.streamOfChildProperty('microphone', 'track')
    ]).multicast()

    this.screen$ = combineStreams(combineTracks, [
      this.streamOfChildProperty('screen', 'track'),
      this.streamOfChildProperty('audio', 'track')
    ]).multicast()

    this.feed$ = combineStreams(combineTracks, [
      this.streamOfChildProperty('videoFeed', 'track'),
      this.streamOfChildProperty('audioFeed', 'track')
    ]).multicast()

    this.webcam$.forEach(x => this.webcamStream = x)
    this.screen$.forEach(x => this.screenStream = x)
    this.feed$.forEach(x => this.feedStream = x)
  },

  updateMediaByOwnDeviceIds () {
    // This is only hit for remote peers
    this.setWebcam(this.webcamDeviceId ? {peerId: this.id, type: 'webcam', deviceId: this.webcamDeviceId} : null)
    this.setMicrophone(this.microphoneDeviceId ? {peerId: this.id, type: 'microphone', deviceId: this.microphoneDeviceId} : null)
    this.setScreen(this.screenDeviceId ? {peerId: this.id, type: 'screen', deviceId: this.screenDeviceId} : null)
    this.setAudio(this.audioDeviceId ? {peerId: this.id, type: 'audio', deviceId: this.audioDeviceId} : null)
  },

  async setWebcam (mediaOrDevice) {
    this.webcam && this.webcam.stopUsingTrack(this.cid)
    if (!mediaOrDevice) {
      return this.unset('webcam')
    }
    const media = await this.addMedia(mediaOrDevice)
    this.webcam = media
    await media && media.useTrack(this.cid)
    return media
  },
  toggleWebcamEnabled () {
    if (result('webcam.locallyPaused')(this)) {
      this.enableWebcam()
    } else {
      this.disableWebcam()
    }
  },
  disableWebcam () {
    result('webcam.producer.pause', this)
  },
  enableWebcam () {
    result('webcam.producer.resume', this)
  },
  async setMicrophone (mediaOrDevice) {
    this.microphone && this.microphone.stopUsingTrack(this.cid)
    if (!mediaOrDevice) {
      return this.unset('microphone')
    }
    const media = await this.addMedia(mediaOrDevice)
    this.microphone = media
    media && media.useTrack(this.cid)
    return media
  },
  toggleMicrophoneEnabled () {
    if (result('microphone.locallyPaused')(this)) {
      this.enableMicrophone()
    } else {
      this.disableMicrophone()
    }
  },
  disableMicrophone () {
    result('microphone.producer.pause')(this)
  },
  enableMicrophone () {
    result('microphone.producer.resume')(this)
  },
  async setScreen (mediaOrDevice) {
    this.screen && this.screen.stopUsingTrack(this.cid)
    if (!mediaOrDevice) {
      return this.unset('screen')
    }
    const media = await this.addMedia(mediaOrDevice)
    this.screen = media
    media && media.useTrack(this.cid)
    return media
  },
  async setAudio (mediaOrDevice) {
    this.audio && this.audio.stopUsingTrack(this.cid)
    if (!mediaOrDevice) {
      return this.unset('audio')
    }
    const media = await this.addMedia(mediaOrDevice)
    this.audio = media
    media && media.useTrack(this.cid)
    return media
  }
})

const PeerCollection = Collection.extend(Observable).extend({
  model: PeerModel,
  event$: {},
  prop$: {},
  initialize () {
    this.on('add remove reset', () => this.trigger('change'))
  },
  scan (watchAttrs = [], reducer = () => {}) {
    if (isString(watchAttrs)) {
      watchAttrs = [watchAttrs]
    }
    return createStream(addToStream => {
      const scanId = _.uniqueId()
      const completeEvent = `scan:complete:${scanId}`
      const complete$ = this.streamOfOneEvent(completeEvent)
      const reduce = () => flow(
        reducer,
        addToStream
      )(this.models)

      const handleNewItem = item => watchAttrs.forEach(key =>
        item
          .streamOfProperty(key)
          .until(complete$)
          .skip(1)
          .forEach(reduce))

      this.streamOfEvent('add').until(complete$).forEach(flow(
        handleNewItem,
        reduce
      ))
      this.streamOfEvent('remove').until(complete$).forEach(reduce)
      this.streamOfEvent('reset').until(complete$).forEach(reduce)
      this.each(handleNewItem)
      reduce()

      // Dispose
      return () => {
        this.trigger(completeEvent)
      }
    })
  }
})

PeerCollection.prototype.filtered = function (matchAttrs = {}) {
  const keys = _.keys(matchAttrs)
  const collection = new PeerCollection()
  const checkMatch = item => _.isMatch(item, matchAttrs)
  const updateItem = item => checkMatch(item) ? collection.add(item) : collection.remove(item)
  const handleNewItem = item => {
    keys.forEach(key => item.on(`change:${key}`, () => updateItem(item)))
    updateItem(item)
  }

  this.each(handleNewItem)
  this.on('add', handleNewItem)
  this.on('remove', item => collection.remove(item))
  this.on('reset', () => collection.reset())

  return collection
}

export {PeerModel, PeerCollection}



// WEBPACK FOOTER //
// ./client/webrtc/greenroom-peer-model.js